import { transformToRomanNumber } from './transform-to-roman-number'

/**
 * Returns the privacy protection text including various sections.
 * Order of sections defines there section number (i.e. I, II, III, IV, ...)
 */
export const getPrivacyProtectionText = (): string => {
  const sections = [
    getNameAndAdressOfResponsiblePerson,
    getNameAndAddressOfDataPrivacyCommissioner,
    getGeneralInformation,
    getWebpageAndLogfiles,
    getCookieUsage,
    getUserRegisration,
    getInstallationInvitation,
    getLocationInquiry,
    getRegistrationViaHandingOutInstallerPlaque,
    getInstallationStatement,
    getEmailNotifications,
    getContactInformation,
    getLuoxEnergy,
    getOstrom,
    getSmartVehicleIntegration,
    getSsiEncryption,
    getRightsOfAffectedPerson,
  ]

  return sections.map((section, index) => section(index + 1)).join('\n')
}

const getNameAndAdressOfResponsiblePerson = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)
  return `
### ${romanSectionNumber} Name und Anschrift des Verantwortlichen

Der Verantwortliche im Sinne der Datenschutz-Grundverordnung und anderer nationaler Datenschutzgesetze der Mitgliedsstaaten sowie sonstiger datenschutzrechtlicher Bestimmungen ist die:

   HagerEnergy GmbH
   Ursula-Flick-Straße 8  
   49076 Osnabrück  
   Deutschland  
   Tel.: 0541 760268599
   E-Mail: datenschutzbeauftragter@e3dc.com  
   Website: [www.e3dc.com](https://www.e3dc.com/)`
}

const getNameAndAddressOfDataPrivacyCommissioner = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)

  return `
### ${romanSectionNumber} Name und Anschrift des Datenschutzbeauftragten

Der Datenschutzbeauftragte des Verantwortlichen ist:

   TÜV Informationstechnik GmbH
   Unternehmensgruppe TÜV NORD GROUP  
   IT Security, Business Security & Privacy  
   Am Tüv 1  
   45307 Essen  
   Telefon 0201 - 8999-899
   Telefax 0201 - 8999-666  
   E-Mail: privacyguard@tuvit.de`
}

const getGeneralInformation = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)

  return `
### ${romanSectionNumber} Allgemeines zur Datenverarbeitung

  #### 1. Umfang der Verarbeitung personenbezogener Daten

Wir erheben und verwenden personenbezogene Daten unserer Nutzer grundsätzlich nur, soweit dies zur Bereitstellung einer funktionsfähigen Website sowie unserer Inhalte und Leistungen erforderlich ist. Die Erhebung und Verwendung personenbezogener Daten unserer Nutzer erfolgt regelmäßig nur nach Einwilligung des Nutzers. Eine Ausnahme gilt in solchen Fällen, in denen eine vorherige Einholung einer Einwilligung aus tatsächlichen Gründen nicht möglich ist und die Verarbeitung der Daten durch gesetzliche Vorschriften gestattet ist.

  #### 2. Rechtsgrundlage für die Verarbeitung personenbezogener Daten

Soweit wir für Verarbeitungsvorgänge personenbezogener Daten eine Einwilligung der betroffenen Person einholen, dient Art. 6 Abs. 1 lit. a EU-Datenschutzgrundverordnung (DSGVO) als Rechtsgrundlage für die Verarbeitung personenbezogener Daten.

Bei der Verarbeitung von personenbezogenen Daten, die zur Erfüllung eines Vertrages, dessen Vertragspartei die betroffene Person ist, erforderlich ist, dient Art. 6 Abs. 1 lit. b DSGVO als Rechtsgrundlage. Dies gilt auch für Verarbeitungsvorgänge, die zur Durchführung vorvertraglicher Maßnahmen erforderlich sind.

Soweit eine Verarbeitung personenbezogener Daten zur Erfüllung einer rechtlichen Verpflichtung erforderlich ist, der unser Unternehmen unterliegt, dient Art. 6 Abs. 1 lit. c DSGVO als Rechtsgrundlage.

Für den Fall, dass lebenswichtige Interessen der betroffenen Person oder einer anderen natürlichen Person eine Verarbeitung personenbezogener Daten erforderlich machen, dient Art. 6 Abs. 1 lit. d DSGVO als Rechtsgrundlage.

Ist die Verarbeitung zur Wahrung eines berechtigten Interesses unseres Unternehmens oder eines Dritten erforderlich und überwiegen die Interessen, Grundrechte und Grundfreiheiten des Betroffenen das erstgenannte Interesse nicht, so dient Art. 6 Abs. 1 lit. f DSGVO als Rechtsgrundlage für die Verarbeitung.

  #### 3. Datenlöschung und Speicherdauer

Die personenbezogenen Daten der betroffenen Person werden gelöscht oder gesperrt, sobald der Zweck der Speicherung entfällt. Eine Speicherung kann darüber hinaus dann erfolgen, wenn dies durch den europäischen oder nationalen Gesetzgeber in unionsrechtlichen Verordnungen, Gesetzen oder sonstigen Vorschriften, denen der Verantwortliche unterliegt, vorgesehen wurde. Eine Sperrung oder Löschung der Daten erfolgt auch dann, wenn eine durch die genannten Normen vorgeschriebene Speicherfrist abläuft, es sei denn, dass eine Erforderlichkeit zur weiteren Speicherung der Daten für einen Vertragsabschluss oder eine Vertragserfüllung besteht.`
}

const getWebpageAndLogfiles = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)

  return `
  ### ${romanSectionNumber} Bereitstellung der Website und Erstellung von Logfiles

  #### 1. Beschreibung und Umfang der Datenverarbeitung

Bei jedem Aufruf unserer Internetseite erfasst unser System automatisiert Daten und Informationen vom Computersystem des aufrufenden Rechners.

Folgende Daten werden hierbei erhoben:

- Informationen über den Browsertyp und die verwendete Version

- Das Betriebssystem des Nutzers

- Die IP-Adresse des Nutzers

- Datum und Uhrzeit des Zugriffs

Die Daten werden ebenfalls in den Logfiles unseres Systems gespeichert. Eine Speicherung dieser Daten zusammen mit anderen personenbezogenen Daten des Nutzers findet nicht statt.

  #### 2. Rechtsgrundlage für die Datenverarbeitung

Rechtsgrundlage für die vorübergehende Speicherung der Daten und der Logfiles ist Art. 6 Abs. 1 lit. f DSGVO.

  #### 3. Zweck der Datenverarbeitung

Die vorübergehende Speicherung der IP-Adresse durch das System ist notwendig, um eine Auslieferung der Website an den Rechner des Nutzers zu ermöglichen. Hierfür muss die IP-Adresse des Nutzers für die Dauer der Sitzung gespeichert bleiben.

Die Speicherung in Logfiles erfolgt, um die Funktionsfähigkeit der Website sicherzustellen. Zudem dienen uns die Daten zur Optimierung der Website und zur Sicherstellung der Sicherheit unserer informationstechnischen Systeme. Eine Auswertung der Daten zu Marketingzwecken findet in diesem Zusammenhang nicht statt.

In diesen Zwecken liegt auch unser berechtigtes Interesse an der Datenverarbeitung nach Art. 6 Abs. 1 lit. f DSGVO.

  #### 4. Dauer der Speicherung

Die Daten werden gelöscht, sobald sie für die Erreichung des Zweckes ihrer Erhebung nicht mehr erforderlich sind. Im Falle der Erfassung der Daten zur Bereitstellung der Website ist dies der Fall, wenn die jeweilige Sitzung beendet ist.

Im Falle der Speicherung der Daten in Logfiles ist dies nach spätestens sieben Tagen der Fall. Eine darüberhinausgehende Speicherung ist möglich. In diesem Fall werden die IP-Adressen der Nutzer gelöscht oder verfremdet, sodass eine Zuordnung des aufrufenden Clients nicht mehr möglich ist.

  #### 5. Widerspruchs- und Beseitigungsmöglichkeit

Die Erfassung der Daten zur Bereitstellung der Website und die Speicherung der Daten in Logfiles ist für den Betrieb der Internetseite zwingend erforderlich. Es besteht folglich seitens des Nutzers keine Widerspruchsmöglichkeit.`
}

const getCookieUsage = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)
  return `
### ${romanSectionNumber} Verwendung von Cookies

  #### 1. Beschreibung und Umfang der Datenverarbeitung

Unsere Webeseite verwendet Cookies. Bei Cookies handelt es sich um Textdateien, die im Internetbrowser bzw. vom Internetbrowser auf dem Computersystem des Nutzers gespeichert werden. Ruft ein Nutzer eine Website auf, so kann ein Cookie auf dem Betriebssystem des Nutzers gespeichert werden. Dieser Cookie enthält eine charakteristische Zeichenfolge, die eine eindeutige Identifizierung des Browsers beim erneuten Aufrufen der Website ermöglicht.

Wir setzen Cookies ein, um unsere Website nutzerfreundlicher zu gestalten. Einige Elemente unserer Internetseite erfordern es, dass der aufrufende Browser auch nach einem Seitenwechsel identifiziert werden kann.

Beim Aufruf unserer Website werden die Nutzer durch einen Infobanner über die Verwendung von Cookies informiert, auf diese Datenschutzerklärung verwiesen und um Zustimmung gebeten. Die Website kann auch mit Einschränkungen bei der User-Experience ohne Cookies verwendet werden.

  #### 2. Rechtsgrundlage für die Datenverarbeitung

Die Rechtsgrundlage für die Verarbeitung personenbezogener Daten unter Verwendung technisch notweniger Cookies ist Art. 6 Abs. 1 lit. f DSGVO.

  #### 3. Zweck der Datenverarbeitung

Die Verwendung der Cookies erfolgt zu dem Zweck, die Qualität unserer Website und ihre Inhalte zu verbessern. Durch die Cookies können wir, die User-Experience insbesondere im Bereich der Ladezeiten und der Visualisierung von Kartendarstellungen verbessern.

In diesen Zwecken liegt auch unser berechtigtes Interesse in der Verarbeitung der personenbezogenen Daten nach Art. 6 Abs. 1 lit. f DSGVO.

  #### 4. Dauer der Speicherung, Widerspruchs- und Beseitigungsmöglichkeit

Cookies werden auf dem Rechner des Nutzers gespeichert und von diesem an unserer Seite übermittelt. Daher haben Sie als Nutzer auch die volle Kontrolle über die Verwendung von Cookies. Durch eine Änderung der Einstellungen in Ihrem Internetbrowser können Sie die Übertragung von Cookies deaktivieren oder einschränken. Bereits gespeicherte Cookies können jederzeit gelöscht werden. Dies kann auch automatisiert erfolgen. Werden Cookies für unsere Website deaktiviert, können möglicherweise nicht mehr alle Funktionen der Website vollumfänglich genutzt werden. Sie können ihren Widerspruch für die Verwendung der einzelnen Cookies über die im User-Profil der Website vorhandene Opt-Out Funktion explizit gelten machen und unsere Website dennoch nutzen.
`
}

const getUserRegisration = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)
  return `
### ${romanSectionNumber} Nutzer registrieren

  #### 1. Beschreibung und Umfang der Datenverarbeitung

Um unser Web-Portal nutzen zu können wird ein registriertes Nutzerkonto benötigt. Für die Neuanlage eines Nutzerprofils können Sie das in der Anmeldemaske zur Verfügung stehende Formular verwenden.

Wir speichern die folgenden Daten zu ihrem Benutzerprofil (* Pflichtfelder)
	- E-Mailadresse*
	- Vorname*
	- Nachname*
	- Straße
	- Postleitzahl
	- Ort
	- Telefon
	- Fax
	- Password*

  Zum Zeitpunkt der Registrierung werden zudem folgende Daten gespeichert:
	- Die IP-Adresse des Nutzers
	- Datum und Uhrzeit der Registrierung

Im Rahmen der Registrierung wird eine Einwilligung des Nutzers zur Verarbeitung dieser Daten eingeholt und die E-Mailadresse mittels Versand eines Aktivierungslinks validiert. Dieser Aktivierungslink hat eine Gültigkeit von 2 Tagen.

  #### 2. Rechtsgrundlage für die Datenverarbeitung

Rechtsgrundlage für die Verarbeitung der Daten ist bei Vorliegen einer Einwilligung des Nutzers Art. 6 Abs. 1 lit. a DSGVO.

  #### 3. Zweck der Datenverarbeitung

Anlage eines gültigen Nutzerprofils im Web-Portal um dessen Funktionen nutzen zu können.

  #### 4. Dauer der Speicherung

Die Daten werden gelöscht, sobald sie für die Erreichung des Zweckes ihrer Erhebung nicht mehr erforderlich sind.

Dies ist für das Nutzerprofil der Fall, wenn das Profil deaktiviert/gelöscht wurde. Sollte die E-Mailadresse nicht via des Aktivierungslinks validiert worden sein, werden die bis dahin erhobenen Daten (E-Mailadresse) automatisch gelöscht.

  #### 5. Widerspruchs- und Beseitigungsmöglichkeit

Für die Deaktivierung und somit Löschung des Nutzerprofiles nehmen sie bitte Kontakt mit unserem Support-Team auf.`
}

const getInstallationInvitation = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)
  return `
### ${romanSectionNumber} Einladung zur Installation über User-Interface des Speichersystems oder über das Web-Portal

  #### 1. Beschreibung und Umfang der Datenverarbeitung

Um unser Web-Portal nutzen zu können wird ein registriertes Nutzerkonto benötigt. Für die Speichersystembesitzer kann über das User-Interface des Speichersystems ein Benutzeraccount für das Web-Portal unter Angabe personenbezogener Daten (E-Mailadresse) eingeladen werden. Diese Funktion steht ebenfalls im Web-Portal über das Rechtemanamgent zur Verfügung. Die Daten werden dabei in eine Eingabemaske eingegeben und an uns übermittelt und gespeichert. Eine Weitergabe der Daten an Dritte findet nicht statt. Folgende Daten werden im Rahmen des Registrierungsprozesses erhoben:
	- E-Mailadresse

Zum Zeitpunkt der Registrierung werden zudem folgende Daten gespeichert:
	- Die IP-Adresse des Nutzers
	- Datum und Uhrzeit der Registrierung
Im Rahmen des Einladungsprozesses wird eine Einwilligung des Nutzers zur Verarbeitung dieser Daten eingeholt.

  #### 2. Rechtsgrundlage für die Datenverarbeitung

Rechtsgrundlage für die Verarbeitung der Daten ist bei Vorliegen einer Einwilligung des Nutzers Art. 6 Abs. 1 lit. a DSGVO.

  #### 3. Zweck der Datenverarbeitung

Für den Einladungsprozess wird der Kunde via E-Mail zur Annahme der Einladung zur Installation durch Klick auf einen Link aufgefordert, um diesen Prozess sicher zu gestalten benötigen wir zur Validierung die obengenannten Daten. Eine Einladung des Nutzers und die potenziell daraus folgende Verknüpfung zwischen Nutzerkonto und Installation ist für das Bereithalten bestimmter Inhalte und Leistungen in unserem Web-Portal erforderlich. Der Kunde kann mit Hilfe des Web-Portals seinen Speicher online überwachen, seine Verbräuche und Energieflüsse monitoren und so auf eine maximale Autarkiequote hinarbeiten.

  #### 4. Dauer der Speicherung

Die Daten werden gelöscht, sobald sie für die Erreichung des Zweckes ihrer Erhebung nicht mehr erforderlich sind.

Dies ist für die Einladung zur Installation erhobenen Daten der Fall, wenn die Einladung angenommen oder abgelehnt wurde.

  #### 5. Widerspruchs- und Beseitigungsmöglichkeit

Die Einladung zur Installation wird als E-Mail versandt, ohne weitere Interaktion erlischt der Aktivierungslink nach 2 Tagen. Die über Sie gespeicherten Daten werden, nachdem der Link erloschen ist automatisch gelöscht. Bei Annahme der Einladung zur Installation wird das Datum dauerhaft gespeichert für Widerspruchs- und Beseitigungswünschen in diesem oder anderen Fällen nehmen sie bitte Kontakt mit unserem Support-Team auf.`
}

const getRegistrationViaHandingOutInstallerPlaque = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)
  return `
### ${romanSectionNumber} Registrierung durch Verleihung der Installateur-Plakette

  #### 1. Beschreibung und Umfang der Datenverarbeitung

Um unser Web-Portal nutzen zu können wird ein registriertes Nutzerkonto benötigt. Für unsere geschulten Installateure findet eine automatische Registrierung des zur Installation eines Systems notwendigen Nutzerkontos nach erfolgreicher Prüfung der Eignung statt. Die Daten, die zur Registrierung verwendet werden, werden dabei der Anmeldung zur Schulung entnommen. Eine Weitergabe der Daten an Dritte findet nicht statt. Folgende Daten werden im Rahmen des Registrierungsprozesses erhoben:
	- Name, Vorname
	- Straße
	- Ort
	- Postleitzahl
	- Firmenzugehörigkeit
	- Telefon
	- Fax
	- E-Mail

Im Rahmen der Schulungsanmeldung wird eine Einwilligung des Nutzers zur Verarbeitung dieser Daten eingeholt.

  #### 2. Rechtsgrundlage für die Datenverarbeitung

Rechtsgrundlage für die Verarbeitung der Daten ist bei Vorliegen einer Einwilligung des Nutzers Art. 6 Abs. 1 lit. a DSGVO.

  #### 3. Zweck der Datenverarbeitung

Eine Registrierung des Nutzers ist für das Bereithalten bestimmter Inhalte und Leistungen auf unseres Web-Portals erforderlich. Der Installateur benötigt diesen Zugang zur Inbetriebnahme der Anlage sowie zur Abgabe der Inbetriebnahme Erklärung.

  #### 4. Dauer der Speicherung

Die Daten werden gelöscht, sobald sie für die Erreichung des Zweckes ihrer Erhebung nicht mehr erforderlich sind.

Dies ist für die während des Registrierungsvorgangs erhobenen Daten der Fall, wenn die Registrierung auf unserer Internetseite aufgehoben oder abgeändert wird

  #### 5. Widerspruchs- und Beseitigungsmöglichkeit

Als Nutzer haben sie jederzeit die Möglichkeit, die Registrierung aufzulösen. Die über Sie gespeicherten Daten können Sie jederzeit abändern lassen.  Hierzu nehmen sie bitte Kontakt mit unserem Support-Team auf.`
}

const getLocationInquiry = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)
  return `
### ${romanSectionNumber} Standortabfrage

  #### 1. Beschreibung und Umfang der Datenverarbeitung

  Um die Funktionen in unserem Web-Portal, unter anderem Prognoseberechnungen anhand des Wetters am Standort des Speichersystems, einwandfrei nutzen zu können wird der Standort des Speichersystems benötigt. Ebenfalls werden die Standortdaten für die Planung von Serviceeinsätzen bei Bedarf benötigt, um den verantwortlichen Servicetechniker ordnungsgemäß einplanen zu können. Die Standortdaten werden in eine Eingabemaske nach dem Login ins Web-Portal eingegeben und an uns übermittelt. Eine Weitergabe an Dritte findet nicht statt. Folgende Daten werden erhoben:   
  - Straße
  - Hausnummer
  - Wohnort
  - PLZ
  - Land

  #### 2. Rechtsgrundlage für die Datenverarbeitung

Rechtsgrundlage für die Verarbeitung der Daten ist Art. 6 Abs. 1 lit. b), c) DSGVO. 

  #### 3. Zweck der Datenverarbeitung

  Die Angabe der Standortdaten ist für das Bereithalten bestimmter Inhalte und Leistungen in unserem Web-Portal erforderlich. Für den Kunden werden Prognosedaten seiner Verbräuche und der Energieflüsse anhand der Standortdaten errechnet. Mit diesen Prognoseberechnungen kann der Kunde auf eine maximale Autarkie hinarbeiten. Weiter sind die Standortdaten unerlässlich für die Planung von Serviceeinsätzen, sollte ein Serviceeinsatz notwendig werden.

  #### 4. Dauer der Speicherung

  Die Daten werden gelöscht, sobald sie für die Erreichung des Zwecks ihrer Erhebung nicht mehr erforderlich sind. 

  Dies ist für die erhobenen Standortdaten der Fall, wenn die Installation aus dem Web-Portal gelöscht wird. 

  #### 5. Widerspruchs- und Beseitigungsmöglichkeit

  Für die Deaktivierung und somit Löschung der Installation nehmen Sie bitte Kontakt mit unserem Support-Team auf.`
}

const getInstallationStatement = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)
  return `
### ${romanSectionNumber} Inbetriebnahme-Erklärung / Garantieverlängerung

  #### 1. Beschreibung und Umfang der Datenverarbeitung

Um die übliche Garantieverlängerung zu beantragen ist eine Inbetriebnahme-Erklärung einzureichen. Für unsere geschulten Installateure ist dieser Prozess im Web-Portal abgebildet. Die in der Inbetriebnahme-Erklärung erhobenen personenbezogenen Daten dienen der Vervollständigung des Nutzer-Kontos, so dass alle Funktionen der Anlage genutzt und ein fehlerfreier Betrieb sichergestellt werden kann. Eine Weitergabe der Daten an Dritte findet nicht statt. Folgende personenbezogene Daten werden im Rahmen der Inbetriebnahme-Erklärung erhoben:
Kundenanschrift:
	- Name, Vorname
	- Straße
	- Ort
	- Postleitzahl
	- Jahressstromverbrauch

Abweichende Installationsadresse:
	- Straße
	- Ort
	- Postleitzahl

Installationsbetrieb:
	- Firma
	- Installateur (Name, Vorname)
	- Straße
	- Ort
	- Postleitzahl
	- E-Mail
	- Telefon
	- Fax

Im Rahmen der Inbetriebnahme-Erklärung wird eine Einwilligung zur Verarbeitung dieser Daten eingeholt.

  #### 2. Rechtsgrundlage für die Datenverarbeitung

Rechtsgrundlage für die Verarbeitung der Daten ist bei Vorliegen einer Einwilligung des Nutzers Art. 6 Abs. 1 lit. a DSGVO.

  #### 3. Zweck der Datenverarbeitung

Eine auf diese Weise erweiterte Registrierung des Nutzers ist für das Bereithalten bestimmter Inhalte und Leistungen auf unseres Web-Portals erforderlich. Mit Hilfe dieser Informationen kann ein Fehlerfall schnellstmöglich erkannt und ein Technikereinsatz geplant werden.

  #### 4. Dauer der Speicherung

Die Daten werden gelöscht, sobald sie für die Erreichung des Zweckes ihrer Erhebung nicht mehr erforderlich sind.

Dies ist für die während des Registrierungsvorgangs erhobenen Daten der Fall, wenn die Registrierung auf unserer Internetseite aufgehoben oder abgeändert wird

  #### 5. Widerspruchs- und Beseitigungsmöglichkeit

Als Nutzer haben sie jederzeit die Möglichkeit, die Registrierung aufzulösen. Die über Sie gespeicherten Daten können Sie jederzeit abändern lassen.  Hierzu nehmen sie bitte Kontakt mit unserem Support-Team auf.`
}

const getEmailNotifications = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)
  return `
### ${romanSectionNumber} E-Mail-Benachrichtigung

  #### 1. Beschreibung und Umfang der Datenverarbeitung

In unserem Web-Portal besteht die Möglichkeit eine kostenfreie E-Mail-Benachrichtigung zu abonnieren. Dabei werden bei der Anmeldung die Daten aus dem bereits hinterlegtem User-Profil genutzt (siehe Registrierung). Diese E-Mail-Benachrichtigung warnt den Kunden bei Eintreten eines Fehlerzustands seines Speichers.

  #### 2. Rechtsgrundlage für die Datenverarbeitung

Rechtsgrundlage für die Verarbeitung der Daten nach Anmeldung zum Newsletter durch den Nutzer ist bei Vorliegen einer Einwilligung des Nutzers Art. 6 Abs. 1 lit. a DSGVO.

  #### 3. Zweck der Datenverarbeitung

Die Erhebung der E-Mail-Adresse des Nutzers dient dazu, die E-Mail-Benachrichtigungen zuzustellen.

Die Erhebung sonstiger personenbezogener Daten im Rahmen des Anmeldevorgangs dient dazu, einen Missbrauch der Dienste oder der verwendeten E-Mail-Adresse zu verhindern.

  #### 4. Dauer der Speicherung

Die Daten werden gelöscht, sobald sie für die Erreichung des Zweckes ihrer Erhebung nicht mehr erforderlich sind.

  #### 5. Widerspruchs- und Beseitigungsmöglichkeit

Das Abonnement der E-Mail-Benachrichtigung kann durch den betroffenen Nutzer jederzeit gekündigt werden. Zu diesem Zweck findet Sie im Web-Portal unter Benutzerprofil die Option E-Mail-Benachrichtigung.`
}

const getContactInformation = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)
  return `
### ${romanSectionNumber} Kontaktformular und E-Mail-Kontakt
  #### 1.	Beschreibung und Umfang der Datenverarbeitung

  In unserem Web-Portal gibt es ein Kontaktformular zur Aufnahme von Servicefällen, dieses kann zur Erstellung eines Service-Tickets genutzt werden. Nimmt ein Nutzer diese Möglichkeit wahr, so werden die eingegebenen Daten mit dem bereits vorhandenen User-Profil (siehe Registrierung) verknüpft. Diese Daten sind:
  - Betreff
  - benutzerdefiniertes Freitextfeld
  - Anlagen
  - Seriennummer (des Geräts)
  - Art der Anfrage

  Im Zeitpunkt der Absendung der Nachricht werden zudem folgende Daten gespeichert:
  - Datum und Uhrzeit des Versands
  
  Alternativ ist eine Kontaktaufnahme über die bereitgestellte E-Mail-Adresse möglich. In diesem Fall werden die mit der E-Mail übermittelten personenbezogenen Daten des Nutzers gespeichert. 

  Eingegebene Kontaktdaten können zur Kontaktaufnahme per Telefon oder E-Mail nach Abschluss eines Service-Tickets zur Einholung von Kundenfeedback und für Qualitätssicherungsmaßnahmen verwendet werden. Die Ergebnisse werden für folgende Qualitätssicherungsmaßnahmen verwendet:
  - Qualitätssicherung der After Sales für die Nutzer
  - Ermittlung von Optimierungspotenzial
  - Kenntnis über Kundenwünsche

  In der E-Mail wird zusätzlich ein Link zu der externen Bewertungsplattform Trustpilot A/S, Pilestraede 58, 5, 1112 Kopenhagen, Dänemark versendet. Bei Verwendung des Links wird darauf hingewiesen, dass Trustpilot in diesem Fall ein eigenständiger verantwortlicher Dritter ist und bei Besuch der Webseite von Trustpilot die Datenschutzbestimmungen von Trustpilot, abrufbar unter [Privacy Policy End User](https://corporate.trustpilot.com/legal/for-reviewers/privacy-policy-end-user/jan-2026), gelten.

  Es erfolgt, ohne explizierte Einstimmung des Nutzers keine Weitergabe der Daten an Dritte. Die Daten werden ausschließlich für die Verarbeitung der Konversation verwendet.

  Die Daten des Nutzers werden ausschließlich natürlichen und juristischen Personen zugänglich gemacht, die diese Daten zur Erfüllung des beabsichtigten Verarbeitungszwecks benötigen. Es steht dem Verantwortlichen frei den Zugriff auf das Service-Ticket durch Dritte zu gewähren, wenn dies zur Erfüllung des beabsichtigten Verarbeitungszwecks erforderlich ist.

  #### 2.	Rechtsgrundlage für die Datenverarbeitung 

  Rechtsgrundlage für die Verarbeitung der Daten ist bei Vorliegen einer Einwilligung des Nutzers Art. 6 Abs. 1 lit. a DSGVO.

  Rechtsgrundlage für die Verarbeitung der Daten, die im Zuge einer Übersendung einer E-Mail übermittelt werden oder zur Kontaktaufnahme zu Qualitätssicherungsmaßnahmen verwendet werden, ist Art. 6 Abs. 1 lit. f DSGVO. Zielt der E-Mail-Kontakt auf den Abschluss eines Vertrages ab, so ist zusätzliche Rechtsgrundlage für die Verarbeitung Art. 6 Abs. 1 lit. b DSGVO.

  #### 3.	Zweck der Datenverarbeitung

  Die Verarbeitung der personenbezogenen Daten aus der Eingabemaske dient uns allein zur Bearbeitung der Kontaktaufnahme. Im Falle einer Kontaktaufnahme per E-Mail liegt hieran auch das erforderliche berechtigte Interesse an der Verarbeitung der Daten.

  Die sonstigen während des Absendevorgangs verarbeiteten personenbezogenen Daten dienen dazu, einen Missbrauch des Kontaktformulars zu verhindern und die Sicherheit unserer informationstechnischen Systeme sicherzustellen.
  
  #### 4.	Dauer der Speicherung

  Die Angaben der Nutzer werden in einem Ticket-System gespeichert.

  Zur Erhaltung der Servicequalität und Sicherstellung des Betriebs der Anlage werden die Daten gespeichert, bis diese nicht mehr erforderlich sind. Diese Daten sind frühestens nach Ablauf der Garantie und spätestens nach Stilllegung der Anlage nicht mehr erforderlich. 

  Wir überprüfen die Erforderlichkeit alle zwei Jahre; Ferner gelten die gesetzlichen Archivierungspflichten.

  Die während des Absendevorgangs zusätzlich erhobenen personenbezogenen Daten werden spätestens nach einer Frist von sieben Tagen gelöscht.

  #### 5.	Widerspruchs- und Beseitigungsmöglichkeit

  Der Nutzer hat jederzeit die Möglichkeit, seine Einwilligung zur Verarbeitung der personenbezogenen Daten zu widerrufen. Der Nutzer hat jederzeit die Möglichkeit sein Einverständnis zur Zugriffsmöglichkeit für Dritte ohne Grund zu widerrufen. Nimmt der Nutzer per E-Mail-Kontakt mit uns auf, so kann er der Speicherung seiner personenbezogenen Daten jederzeit widersprechen. In einem solchen Fall kann die Serviceabwicklung nicht fortgeführt werden.

  Der Widerruf der Einwilligung sowie der Widerspruch der Speicherung sollte per E-Mail an die obenstehende E-Mail-Adresse geschehen.

  Alle personenbezogenen Daten, die im Zuge der Kontaktaufnahme gespeichert wurden, werden in diesem Fall gelöscht.`
}

const getLuoxEnergy = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)
  return `
### ${romanSectionNumber} LUOX Energy Integration

  #### 1. Beschreibung und Umfang der Datenverarbeitung

  In unserem Web-Portal besteht die Möglichkeit auf die Vertragsabschlussstrecke des Anbieters LUOX Energy der Lumenaza GmbH für Direktvermarktung 
  und dynamische Strompreise zu kommen.

  ##### a) Direktvermarktung
  Bei der Direktvermarktung über einen Direktvermarkter, wird der eingespeiste Strom von eben diesem an der Strombörse gehandelt. Mit dem Wechsel zur Direktvermarktung geht der Direktvermarkter in eine Kontroll- und Steuerungspflicht. Er muss in der Lage sein die PV-Anlage im Bedarfsfalle abzuregeln und so Netzeinspeisung zu vermeiden. 
  Um diese Funktion so einfach wie möglich und ohne zusätzliche Hardware von LUOX Energy beim Kunden möglich zu machen, bekommt die LUOX Energy Zugriff auf die Leistungswerte und einer speziellen Steuerungs-API zur Abregelung der Anlage. 
  Für den Kunden wird dieser Zugang automatisch bei der gewünschten Zusammenarbeit mit LUOX Energy angelegt und ist in der Rechteverwaltung unter den Cloud-Registry-Keys transparent abgebildet.

  ##### b) Dynamische Strompreise
  Schließt der Nutzer einen Stromvertrag mit LOUX Energy, so ermöglicht er es der HagerEnergy GmbH für diesen Stromvertrag eine automatische Abfrage der Börsenstrompreise inkl. standortspezifischer Netzentgelte, Abgaben und Steuern durchzuführen. 
  Die Angaben werden sodann an das Energy Management System weitergeleitet, welches das Lade- und Nutzungsverhalten auf dieser Basis optimieren kann.

  #### 2. Rechtsgrundlage für die Datenverarbeitung

  Rechtsgrundlage für die Verarbeitung der Daten ist die Einwilligung des Nutzers Art. 6 Abs. 1 lit. a DSGVO vor dem Absprung zum von LUOX Energy bereitgestelltem Erlösrechner und / oder zum Vertrag mit LOUX Energy.
  
  #### 3. Zweck der Datenverarbeitung

  ##### a) Direktvermarktung
  Nutzung der Cloud-to-Cloud Integration zwischen unserem Web-Portal und der LUOX Energy zur Bereitstellung der Direktvermarktungsdienstleistung ohne zusätzlicher lokaler Mess- und Steuereinrichtungen von LUOX Energy selbst.

  ##### b) Dynamische Strompreise
  Die Weiterleitung der Strompreise an das Energy Management System zur Lade- und Nutzungsoptimierung für Systeme, dessen Nutzer einen Stromvertrag mit LUOX Energy geschlossenen hat. 
  Zur Darstellung der Wirtschaftlichkeit und der Optimierungsmöglichkeiten des Energy Management Systems werden ebenso historische Strompreise, gesetzte Preislimits und Referenzpreise übermittelt. Auf Grundlage dieser Daten können auch mögliche Entwicklungen und zukünftige Prognosemodelle abgebildet werden.
  
  #### 4. Dauer der Speicherung

  Die Daten und der Zugang werden gelöscht, sobald sie für die Erreichung des Zweckes ihrer Erhebung nicht mehr erforderlich sind.
  
  #### 5. Widerspruchs- und Beseitigungsmöglichkeit

  Wiederspruch ist in diesem Fall schriftlich über unseren Support und in Abstimmung mit LUOX Energy möglich, da die Vertragserfüllung von LUOX Energy nur mithilfe dieser Freigabe sichergestellt werden kann. Die über Sie gespeicherten Daten können Sie jederzeit abändern lassen.  `
}

const getOstrom = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)
  return `
### ${romanSectionNumber} Ostrom Integration

#### 1. Beschreibung und Umfang der Datenverarbeitung
In unserem Web-Portal besteht die Möglichkeit auf die Vertragsabschlussstrecke des Anbieters Ostrom GmbH für dynamische Stromtarife zu kommen. Schließt der Nutzer einen Stromvertrag mit der Ostrom GmbH, so ermöglicht er es der HagerEnergy GmbH für diesen Stromvertrag eine automatische Abfrage der Börsenstrompreise inkl. standortspezifischer Netzentgelte, Abgaben und Steuern durchzuführen.  Die Angaben werden sodann an das Energy Management System weitergeleitet, welches das Lade- und Nutzungsverhalten auf dieser Basis optimieren kann.

#### 2. Rechtsgrundlage für die Datenverarbeitung
Rechtsgrundlage für die Verarbeitung der Daten ist die Einwilligung des Nutzers Art. 6 Abs. 1 lit. a DSGVO zum Vertrag mit der Ostrom GmbH.

#### 3. Zweck der Datenverarbeitung
Die Weiterleitung der Strompreise an das Energy Management System zur Lade- und Nutzungsoptimierung für Systeme, dessen Nutzer einen Stromvertrag mit der Ostrom GmbH geschlossenen hat. Zur Darstellung der Wirtschaftlichkeit und der Optimierungsmöglichkeiten des Energy Management Systems werden ebenso historische Strompreise, gesetzte Preislimits und Referenzpreise übermittelt. Auf Grundlage dieser Daten können auch mögliche Entwicklungen und zukünftige Prognosemodelle abgebildet werden.

#### 4. Dauer der Speicherung
Die Daten werden gelöscht, sobald sie für die Erreichung des Zweckes ihrer Erhebung nicht mehr erforderlich sind. 

#### 5. Widerspruchs- und Beseitigungsmöglichkeit
Wiederspruch ist in diesem Fall schriftlich über unseren Support und in Abstimmung mit der Ostrom GmbH möglich. Die über Sie gespeicherten Daten können Sie jederzeit abändern lassen.
`
}

const getSmartVehicleIntegration = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)
  return `
### ${romanSectionNumber} Smarte Fahrzeugintegration über die Energy Hub Alliance GmbH

  #### 1. Beschreibung und Umfang der Datenverarbeitung

In unserem Web-Portal wird bei Einrichtung der smarten Fahrzeugintegration auf das Portal des jeweiligen Fahrzeugherstellers weitergeleitet für die Verknüpfung des Fahrzeugs mit unserem Web-Portal. Diese Verknüpfung ermöglicht es der HagerEnergy GmbH eine automatische Abfrage des Ladezustands, -verbrauchs, -vorgangs des Fahrzeugs durchzuführen. Die Angaben werden sodann an das Energy Management System weitergeleitet, welches das Lade- und Nutzungsverhalten des Fahrzeugs auf dieser Basis optimieren kann.

  #### 2. Rechtsgrundlage für die Datenverarbeitung

Rechtsgrundlage für die Datenverarbeitung ist die Einwilligung des Nutzers gemäß Art. 6 Abs. 1 lit. a DSGVO zur smarten Fahrzeugintegration über die Energy Hub Alliance GmbH.

  #### 3. Zweck der Datenverarbeitung

Die Nutzung der smarten Fahrzeugintegration und Weiterleitung der Ladeinformationen des Fahrzeugs an das Energy Management System zur Lade- und Nutzungsoptimierung des Fahrzeugs des Nutzers. Zur Darstellung der Wirtschaftlichkeit und der Optimierungsmöglichkeiten des Energy Management Systems werden ebenso historische Ladeinformationen, gesetzte Ladelimits und Ladereferenzen des Fahrzeugs übermittelt. Auf Grundlage dieser Daten können auch mögliche Entwicklungen und zukünftige Prognosemodelle abgebildet werden.

  #### 4. Dauer der Speicherung

Die Daten werden gelöscht, sobald sie für die Erreichung des Zweckes ihrer Erhebung nicht mehr erforderlich sind.

  #### 5. Widerspruchs- und Beseitigungsmöglichkeit

Widerspruch ist in diesem Fall schriftlich über unseren Support und in Abstimmung mit der Energy Hub Alliance GmbH möglich. Die über Sie gespeicherten Daten können Sie jederzeit abändern lassen.`
}

const getSsiEncryption = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)
  return `
### ${romanSectionNumber} SSL-Verschlüsselung

Diese Seite nutzt aus Gründen der Sicherheit und zum Schutz der Übertragung vertraulicher Inhalte, wie zum Beispiel der Anfragen, die Sie an uns als Seitenbetreiber senden, eine SSL-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von „http://“ auf „https://“ wechselt und an dem Schloss-Symbol in Ihrer Browserzeile. Wenn die SSL-Verschlüsselung aktiviert ist, können die Daten, die Sie an uns übermitteln, nicht von Dritten mitgelesen werden.
`
}

const getRightsOfAffectedPerson = (sectionNumber: number): string => {
  const romanSectionNumber = transformToRomanNumber(sectionNumber)
  return `
### ${romanSectionNumber} Rechte der betroffenen Person

Werden personenbezogene Daten von Ihnen verarbeitet, sind Sie Betroffener i.S.d. DSGVO und es stehen Ihnen folgende Rechte gegenüber dem Verantwortlichen zu:

  #### 1. Auskunftsrecht

Sie können von dem Verantwortlichen eine Bestätigung darüber verlangen, ob personenbezogene Daten, die Sie betreffen, von uns verarbeitet werden.

Liegt eine solche Verarbeitung vor, können Sie von dem Verantwortlichen über folgende Informationen Auskunft verlangen:

die Zwecke, zu denen die personenbezogenen Daten verarbeitet werden;

die Kategorien von personenbezogenen Daten, welche verarbeitet werden;

die Empfänger bzw. die Kategorien von Empfängern, gegenüber denen die Sie betreffenden personenbezogenen Daten offengelegt wurden oder noch offengelegt werden;

die geplante Dauer der Speicherung der Sie betreffenden personenbezogenen Daten oder, falls konkrete Angaben hierzu nicht möglich sind, Kriterien für die Festlegung der Speicherdauer;

das Bestehen eines Rechts auf Berichtigung oder Löschung der Sie betreffenden personenbezogenen Daten, eines Rechts auf Einschränkung der Verarbeitung durch den Verantwortlichen oder eines Widerspruchsrechts gegen diese Verarbeitung;

das Bestehen eines Beschwerderechts bei einer Aufsichtsbehörde;

alle verfügbaren Informationen über die Herkunft der Daten, wenn die personenbezogenen Daten nicht bei der betroffenen Person erhoben werden;

das Bestehen einer automatisierten Entscheidungsfindung einschließlich Profiling gemäß Art. 22 Abs. 1 und 4 DSGVO und – zumindest in diesen Fällen – aussagekräftige Informationen über die involvierte Logik sowie die Tragweite und die angestrebten Auswirkungen einer derartigen Verarbeitung für die betroffene Person.

Ihnen steht das Recht zu, Auskunft darüber zu verlangen, ob die Sie betreffenden personenbezogenen Daten in ein Drittland oder an eine internationale Organisation übermittelt werden. In diesem Zusammenhang können Sie verlangen, über die geeigneten Garantien gem. Art. 46 DSGVO im Zusammenhang mit der Übermittlung unterrichtet zu werden.

  #### 2. Recht auf Berichtigung

Sie haben ein Recht auf Berichtigung und/oder Vervollständigung gegenüber dem Verantwortlichen, sofern die verarbeiteten personenbezogenen Daten, die Sie betreffen, unrichtig oder unvollständig sind. Der Verantwortliche hat die Berichtigung unverzüglich vorzunehmen.

  #### 3. Recht auf Einschränkung der Verarbeitung

Unter den folgenden Voraussetzungen können Sie die Einschränkung der Verarbeitung der Sie betreffenden personenbezogenen Daten verlangen:

wenn Sie die Richtigkeit der Sie betreffenden personenbezogenen für eine Dauer bestreiten, die es dem Verantwortlichen ermöglicht, die Richtigkeit der personenbezogenen Daten zu überprüfen;

die Verarbeitung unrechtmäßig ist und Sie die Löschung der personenbezogenen Daten ablehnen und stattdessen die Einschränkung der Nutzung der personenbezogenen Daten verlangen;

der Verantwortliche die personenbezogenen Daten für die Zwecke der Verarbeitung nicht länger benötigt, Sie diese jedoch zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen benötigen, oder

wenn Sie Widerspruch gegen die Verarbeitung gemäß Art. 21 Abs. 1 DSGVO eingelegt haben und noch nicht feststeht, ob die berechtigten Gründe des Verantwortlichen gegenüber Ihren Gründen überwiegen.

Wurde die Verarbeitung der Sie betreffenden personenbezogenen Daten eingeschränkt, dürfen diese Daten – von ihrer Speicherung abgesehen – nur mit Ihrer Einwilligung oder zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen oder zum Schutz der Rechte einer anderen natürlichen oder juristischen Person oder aus Gründen eines wichtigen öffentlichen Interesses der Union oder eines Mitgliedstaats verarbeitet werden.

Wurde die Einschränkung der Verarbeitung nach den o.g. Voraussetzungen eingeschränkt, werden Sie von dem Verantwortlichen unterrichtet bevor die Einschränkung aufgehoben wird.

  #### 4. Recht auf Löschung

  ##### a) Löschungspflicht

Sie können von dem Verantwortlichen verlangen, dass die Sie betreffenden personenbezogenen Daten unverzüglich gelöscht werden, und der Verantwortliche ist verpflichtet, diese Daten unverzüglich zu löschen, sofern einer der folgenden Gründe zutrifft:

Die Sie betreffenden personenbezogenen Daten sind für die Zwecke, für die sie erhoben oder auf sonstige Weise verarbeitet wurden, nicht mehr notwendig.

Sie widerrufen Ihre Einwilligung, auf die sich die Verarbeitung gem. Art. 6 Abs. 1 lit. a oder Art. 9 Abs. 2 lit. a DSGVO stützte, und es fehlt an einer anderweitigen Rechtsgrundlage für die Verarbeitung.

Sie legen gem. Art. 21 Abs. 1 DSGVO Widerspruch gegen die Verarbeitung ein und es liegen keine vorrangigen berechtigten Gründe für die Verarbeitung vor, oder Sie legen gem. Art. 21 Abs. 2 DSGVO Widerspruch gegen die Verarbeitung ein.

Die Sie betreffenden personenbezogenen Daten wurden unrechtmäßig verarbeitet.

Die Löschung der Sie betreffenden personenbezogenen Daten ist zur Erfüllung einer rechtlichen Verpflichtung nach dem Unionsrecht oder dem Recht der Mitgliedstaaten erforderlich, dem der Verantwortliche unterliegt.

Die Sie betreffenden personenbezogenen Daten wurden in Bezug auf angebotene Dienste der Informationsgesellschaft gemäß Art. 8 Abs. 1 DSGVO erhoben.

  ##### b) Information an Dritte

Hat der Verantwortliche die Sie betreffenden personenbezogenen Daten öffentlich gemacht und ist er gem. Art. 17 Abs. 1 DSGVO zu deren Löschung verpflichtet, so trifft er unter Berücksichtigung der verfügbaren Technologie und der Implementierungskosten angemessene Maßnahmen, auch technischer Art, um für die Datenverarbeitung Verantwortliche, die die personenbezogenen Daten verarbeiten, darüber zu informieren, dass Sie als betroffene Person von ihnen die Löschung aller Links zu diesen personenbezogenen Daten oder von Kopien oder Replikationen dieser personenbezogenen Daten verlangt haben.

  ##### c) Ausnahmen

Das Recht auf Löschung besteht nicht, soweit die Verarbeitung erforderlich ist

zur Ausübung des Rechts auf freie Meinungsäußerung und Information;

zur Erfüllung einer rechtlichen Verpflichtung, die die Verarbeitung nach dem Recht der Union oder der Mitgliedstaaten, dem der Verantwortliche unterliegt, erfordert, oder zur Wahrnehmung einer Aufgabe, die im öffentlichen Interesse liegt oder in Ausübung öffentlicher Gewalt erfolgt, die dem Verantwortlichen übertragen wurde;

aus Gründen des öffentlichen Interesses im Bereich der öffentlichen Gesundheit gemäß Art. 9 Abs. 2 lit. h und i sowie Art. 9 Abs. 3 DSGVO;

für im öffentlichen Interesse liegende Archivzwecke, wissenschaftliche oder historische Forschungszwecke oder für statistische Zwecke gem. Art. 89 Abs. 1 DSGVO, soweit das unter Abschnitt a) genannte Recht voraussichtlich die Verwirklichung der Ziele dieser Verarbeitung unmöglich macht oder ernsthaft beeinträchtigt, oder

zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen.

  #### 5. Recht auf Unterrichtung

Haben Sie das Recht auf Berichtigung, Löschung oder Einschränkung der Verarbeitung gegenüber dem Verantwortlichen geltend gemacht, ist dieser verpflichtet, allen Empfängern, denen die Sie betreffenden personenbezogenen Daten offengelegt wurden, diese Berichtigung oder Löschung der Daten oder Einschränkung der Verarbeitung mitzuteilen, es sei denn, dies erweist sich als unmöglich oder ist mit einem unverhältnismäßigen Aufwand verbunden.

Ihnen steht gegenüber dem Verantwortlichen das Recht zu, über diese Empfänger unterrichtet zu werden.

  #### 6. Recht auf Datenübertragbarkeit

Sie haben das Recht, die Sie betreffenden personenbezogenen Daten, die Sie dem Verantwortlichen bereitgestellt haben, in einem strukturierten, gängigen und maschinenlesbaren Format zu erhalten. Außerdem haben Sie das Recht diese Daten einem anderen Verantwortlichen ohne Behinderung durch den Verantwortlichen, dem die personenbezogenen Daten bereitgestellt wurden, zu übermitteln, sofern

die Verarbeitung auf einer Einwilligung gem. Art. 6 Abs. 1 lit. a DSGVO oder Art. 9 Abs. 2 lit. a DSGVO oder auf einem Vertrag gem. Art. 6 Abs. 1 lit. b DSGVO beruht und

die Verarbeitung mithilfe automatisierter Verfahren erfolgt.

In Ausübung dieses Rechts haben Sie ferner das Recht, zu erwirken, dass die Sie betreffenden personenbezogenen Daten direkt von einem Verantwortlichen einem anderen Verantwortlichen übermittelt werden, soweit dies technisch machbar ist. Freiheiten und Rechte anderer Personen dürfen hierdurch nicht beeinträchtigt werden.

Das Recht auf Datenübertragbarkeit gilt nicht für eine Verarbeitung personenbezogener Daten, die für die Wahrnehmung einer Aufgabe erforderlich ist, die im öffentlichen Interesse liegt oder in Ausübung öffentlicher Gewalt erfolgt, die dem Verantwortlichen übertragen wurde.

  #### 7. Widerspruchsrecht

Sie haben das Recht, aus Gründen, die sich aus ihrer besonderen Situation ergeben, jederzeit gegen die Verarbeitung der Sie betreffenden personenbezogenen Daten, die aufgrund von Art. 6 Abs. 1 lit. e oder f DSGVO erfolgt, Widerspruch einzulegen; dies gilt auch für ein auf diese Bestimmungen gestütztes Profiling.

Der Verantwortliche verarbeitet die Sie betreffenden personenbezogenen Daten nicht mehr, es sei denn, er kann zwingende schutzwürdige Gründe für die Verarbeitung nachweisen, die Ihre Interessen, Rechte und Freiheiten überwiegen, oder die Verarbeitung dient der Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen.

Werden die Sie betreffenden personenbezogenen Daten verarbeitet, um Direktwerbung zu betreiben, haben Sie das Recht, jederzeit Widerspruch gegen die Verarbeitung der Sie betreffenden personenbezogenen Daten zum Zwecke derartiger Werbung einzulegen; dies gilt auch für das Profiling, soweit es mit solcher Direktwerbung in Verbindung steht.

Widersprechen Sie der Verarbeitung für Zwecke der Direktwerbung, so werden die Sie betreffenden personenbezogenen Daten nicht mehr für diese Zwecke verarbeitet.

Sie haben die Möglichkeit, im Zusammenhang mit der Nutzung von Diensten der Informationsgesellschaft – ungeachtet der Richtlinie 2002/58/EG – Ihr Widerspruchsrecht mittels automatisierter Verfahren auszuüben, bei denen technische Spezifikationen verwendet werden.

  #### 8. Recht auf Widerruf der datenschutzrechtlichen Einwilligungserklärung

Sie haben das Recht, Ihre datenschutzrechtliche Einwilligungserklärung jederzeit zu widerrufen. Durch den Widerruf der Einwilligung wird die Rechtmäßigkeit der aufgrund der Einwilligung bis zum Widerruf erfolgten Verarbeitung nicht berührt.

  #### 9. Automatisierte Entscheidung im Einzelfall einschließlich Profiling

Sie haben das Recht, nicht einer ausschließlich auf einer automatisierten Verarbeitung – einschließlich Profiling – beruhenden Entscheidung unterworfen zu werden, die Ihnen gegenüber rechtliche Wirkung entfaltet oder Sie in ähnlicher Weise erheblich beeinträchtigt. Dies gilt nicht, wenn die Entscheidung

für den Abschluss oder die Erfüllung eines Vertrags zwischen Ihnen und dem Verantwortlichen erforderlich ist,

aufgrund von Rechtsvorschriften der Union oder der Mitgliedstaaten, denen der Verantwortliche unterliegt, zulässig ist und diese Rechtsvorschriften angemessene Maßnahmen zur Wahrung Ihrer Rechte und Freiheiten sowie Ihrer berechtigten Interessen enthalten oder

mit Ihrer ausdrücklichen Einwilligung erfolgt.

Allerdings dürfen diese Entscheidungen nicht auf besonderen Kategorien personenbezogener Daten nach Art. 9 Abs. 1 DSGVO beruhen, sofern nicht Art. 9 Abs. 2 lit. a oder g gilt und angemessene Maßnahmen zum Schutz der Rechte und Freiheiten sowie Ihrer berechtigten Interessen getroffen wurden.

Hinsichtlich der in (1) und (3) genannten Fälle trifft der Verantwortliche angemessene Maßnahmen, um die Rechte und Freiheiten sowie Ihre berechtigten Interessen zu wahren, wozu mindestens das Recht auf Erwirkung des Eingreifens einer Person seitens des Verantwortlichen, auf Darlegung des eigenen Standpunkts und auf Anfechtung der Entscheidung gehört.

  #### 10. Recht auf Beschwerde bei einer Aufsichtsbehörde

Unbeschadet eines anderweitigen verwaltungsrechtlichen oder gerichtlichen Rechtsbehelfs steht Ihnen das Recht auf Beschwerde bei einer Aufsichtsbehörde, insbesondere in dem Mitgliedstaat ihres Aufenthaltsorts, ihres Arbeitsplatzes oder des Orts des mutmaßlichen Verstoßes, zu, wenn Sie der Ansicht sind, dass die Verarbeitung der Sie betreffenden personenbezogenen Daten gegen die DSGVO verstößt. Die Aufsichtsbehörde, bei der die Beschwerde eingereicht wurde, unterrichtet den Beschwerdeführer über den Stand und die Ergebnisse der Beschwerde einschließlich der Möglichkeit eines gerichtlichen Rechtsbehelfs nach Art. 78 DSGVO.`
}
